import api from './api';

export interface Threshold { metric: string; green: number; yellow: number; lower_is_better: number }
export interface Weights { turn_weight: number; ontime_weight: number; prepull_credit: number }
export interface MarketGoal { market: string; band: string; effective_date: string; turns_per_truck: number }
export interface DispatcherGoal { dispatcher: string; market: string; band: string; effective_date: string; turns_per_truck_override: number | null }
export interface Dispatcher { username: string; name: string; region: string; market: string; suggested_band: string | null }
export interface PlanBand { enabled: boolean; turns_per_truck: number }
export interface PlanMarket { default_turns_per_truck: number | null; bands: Record<string, PlanBand> }
export interface PlanDispatcher {
  category: string;
  custom_goal: number | null;
  /** Logins this desk covers on ORDERS, where OTD is attributed. Empty means
   *  it answers to its own name. Many-to-many: a desk may cover several, and
   *  several desks may cover one. */
  order_dispatchers?: string[];
}
export interface PlanThreshold { green: number; yellow: number; lower_is_better: boolean }
export interface WinDayPlan {
  markets: Record<string, PlanMarket>;
  dispatchers: Record<string, PlanDispatcher>;
  thresholds: Record<string, PlanThreshold>;
  weights: Weights;
}
export interface Settings {
  /** Every login an order's dispatcher could be, with the name behind it. */
  /** `on_orders` marks the logins carrying open orders now; the rest are
   *  active dispatchers kept a keystroke away rather than dropped. */
  order_dispatchers: { username: string; name: string | null; on_orders: boolean }[];
  /** Every saved plan, newest first. */
  plans: SavedPlan[];
  markets: string[]; bands: string[]; dispatchers: Dispatcher[];
  market_goals: MarketGoal[]; dispatcher_goals: DispatcherGoal[];
  thresholds: Threshold[]; weights: Weights;
  plan: WinDayPlan; plan_effective_date: string | null;
}
export interface DayRow {
  date: string; dispatcher: string; name: string; market: string; band: string; band_goal: number | null;
  category_source: string;
  period_days?: number;
  roster: number; trucks_used: number; turns: Record<string, number>;
  prepulls_lfd: number; goal_trucks: number; turn_score: number | null; appts: number;
  ontime: number; ontime_pct: number | null; loads: number; moves: number;
  /** Trucks the turn score averaged over, and their turns. Smaller than
   *  trucks_used wherever a yard truck worked: yard work is not turn work. */
  scored_drivers: number; scored_turns: number;
  /** Delivery legs only. `loads` counts the delivery leg and the one after
   *  it, so it is roughly twice the containers actually delivered. */
  deliveries: number;
  drivers_on_duty: number; duty_hours_capped: number; drive_hours_capped: number;
  /** Denominator of drive_util -- the Drive detail must divide by this, not by
   *  drivers_on_duty, or the hours shown contradict the chip above them. */
  drivers_driving: number;
  duty_util: number | null; drive_util: number | null; avg_start_min: number | null;
  excess_duty_drivers: number; revenue: number | null; cost: number | null;
  pay_total: number | null; avg_pay: number | null;
  street_turns: number | null; linehaul_prefuel: number | null; demurrage: number | null;
  score: number | null; status: string;
  /** Hours colour, decided with THAT day's duty threshold. */
  duty_status: string;
}
export interface Summary {
  key: string; name: string; dispatcher_count: number; working_day_count: number;
  trucks_used: number; trucks_per_day: number | null; goal_trucks: number;
  turns: number; turns_per_day: number | null;
  appts: number; ontime: number; loads: number; deliveries: number; moves: number; prepulls_lfd: number;
  turn_score: number | null; ontime_pct: number | null;
  drivers_on_duty: number;
  score: number | null; duty_util: number | null; drive_util: number | null;
  revenue: number | null; cost: number | null; margin: number | null;
  linehaul_prefuel: number | null; demurrage: number | null; demurrage_cpc: number | null;
  street_turns: number | null; street_pct: number | null; status: string;
}
/** A row of the saved-plan list. `in_use` marks the one the report reads --
 *  the newest, whatever its effective date. */
/** Selector value standing for the dateless default plan. Not a date, so it
 *  can never collide with one. */
export const DEFAULT_PLAN = 'default';

export interface SavedPlan {
  /** null for the dateless default plan. */
  effective_date: string | null;
  updated_by: string | null;
  updated_at: string | null;
  dispatchers: number;
  /** The plan the report is reading: the newest dated one, or the default. */
  current: boolean;
  /** The protected baseline: the report falls back to it, so it cannot be deleted. */
  is_default: boolean;
}

export interface DriverRow {
  /** What the desk declared this truck was doing that day, from cms.driverstatus
   *  (the Working/Not Working board), falling back to the unit's out-of-service
   *  flag. Independent of whether the truck actually moved anything. */
  status: string;
  category: string | null;
  confirmed: boolean;
  unit: string; name: string; date: string; dispatcher: string;
  turns: Record<string, number>; turns_total: number;
  loads: number; moves: number; prepulls_lfd: number; other_legs: number;
  duty_hours: number | null; drive_hours: number | null;
  first_on_duty_min: number | null; pay: number | null;
  /** This truck's own attainment against the desk's goal, capped at 1.0. */
  turn_pct: number | null;
  load_pros: { pro: number; leg: 'Delivery' | 'Return'; on_time: boolean | null; appt_date: string | null;
    arrived: string; miles: number | null; customer: string | null;
    origin: string | null; destination: string | null; pay: number | null }[];
  prepull_pros: { pro: number; lfd: string; arrived: string }[];
  other_pros: { pro: number; leg: string; arrived: string; customer: string | null;
    origin: string | null; destination: string | null; pay: number | null }[];
}
export interface DriverBreakdown { day: string; market: string; dispatcher: string; desk: DayRow | null; rows: DriverRow[] }
export interface ConditionImage { id: number; name: string }
export interface DailyCondition { market: string; day: string; conditions: string; condition_status: 'green'|'yellow'|'red'; days_out: number | null; author?: string; updated_at?: string; image_count?: number; images?: ConditionImage[] }
export interface MarketConditionCard {
  market: string; day: string; conditions: string;
  condition_status: 'green'|'yellow'|'red'; days_out: number | null;
  author: string | null; updated_at: string | null; image_count: number;
  images: ConditionImage[];
}
/** Which plan scored the day: a dated one, or the dateless default. */
/** A plan the report can be read against. `effective_date: null` is the default. */
export interface PlanChoice { effective_date: string | null; is_default: boolean }

export interface DispatcherReport {
  day: string; market: string; record_days: string[]; rows: DayRow[]; current: DayRow[];
  condition: MarketConditionCard | null;
  summary: Summary[];
  /** The market rolled up per day across the record window, `key` being the
   *  date. Rolled up, not averaged across desks -- see the endpoint. */
  market_days: Summary[];
  thresholds: Record<string, Threshold>; weights: Weights;
  notices: string[];
  markets: string[];
}
export interface ExecutiveReport {
  day: string; window: number; rows: DayRow[]; markets: Summary[]; mtd_markets: Summary[];
  company: Summary | null; mtd: Summary | null; conditions: MarketConditionCard[];
  thresholds: Record<string, Threshold>; weights: Weights;
  notices: string[];
}

export const winTheDayService = {
  /** `DEFAULT_PLAN` asks for the dateless baseline instead of a dated plan. */
  settings: async (effectiveDate?: string) => (await api.get<Settings>('/win-the-day/settings', {
    params: effectiveDate === DEFAULT_PLAN ? { default: true }
          : effectiveDate ? { effective_date: effectiveDate } : {},
  })).data,
  /** `moveFrom` is the date the plan is saved under today. Omitted for a new
   *  plan, which may not land on a date already taken; different from
   *  `effectiveDate` to re-date an existing one. */
  savePlan: (effectiveDate: string, plan: WinDayPlan, moveFrom?: string) =>
    api.put('/win-the-day/settings/plan', {
      effective_date: effectiveDate === DEFAULT_PLAN ? null : effectiveDate,
      plan, move_from: moveFrom && moveFrom !== DEFAULT_PLAN ? moveFrom : null }),
  // Copy and delete ride the plan save endpoint, and the list rides GET
  // /settings. Each new path+method is a separate api_endpoints row needing a
  // grant ticked by hand, and all of this is the one settings page.
  deletePlan: (effectiveDate: string) =>
    api.put('/win-the-day/settings/plan', { effective_date: effectiveDate, action: 'delete' }),
  copyPlan: (from: string, to: string) =>
    api.put('/win-the-day/settings/plan', {
      effective_date: to === DEFAULT_PLAN ? null : to, action: 'copy',
      copy_from: from === DEFAULT_PLAN ? null : from }),
  // Dispatcher assignments save on their own; the plan save leaves them alone.
  saveDispatchers: (effectiveDate: string, dispatchers: Record<string, PlanDispatcher>) =>
    api.put('/win-the-day/settings/dispatchers', {
      effective_date: effectiveDate === DEFAULT_PLAN ? null : effectiveDate, dispatchers }),
  /** `plan` scores every day against one chosen plan instead of the one in
   *  force on each; omitted means per-day, which is the normal reading. */
  /** `part` splits the page: 'current' is the scored day and arrives in a few
   *  seconds, 'record' is the strip behind it and does not. */
  /** `part` splits the page: 'current' is the scored day and arrives in a few
   *  seconds, 'record' is the strip behind it and does not.
   *
   *  `day` is omitted for the record pass. That window is anchored on today,
   *  not on the day being scored, so sending one would make the request look
   *  day-dependent and refetch fourteen seconds of identical rows every time
   *  the date changed. */
  dispatcher: async (day: string | undefined, market: string, window: number, plan?: string, part?: 'current' | 'record') =>
    (await api.get<DispatcherReport>('/win-the-day/dispatcher', { params: { ...(day ? { day } : {}), market, window, ...(plan ? { plan } : {}), ...(part ? { part } : {}) }, timeout: 180000 })).data,
  executive: async (day: string, window: number, plan?: string) =>
    (await api.get<ExecutiveReport>('/win-the-day/executive', { params: { day, window, ...(plan ? { plan } : {}) }, timeout: 180000 })).data,
  // Per-truck detail behind one desk's day. A mode of the report endpoint, not
  // a route of its own -- a new path would arrive deny-by-default and need its
  // own grant ticked before anyone could open the drawer.
  dispatcherDrivers: async (day: string, market: string, dispatcher: string) =>
    (await api.get<DriverBreakdown>('/win-the-day/dispatcher',
      { params: { day, market, drivers_for: dispatcher }, timeout: 180000 })).data,
  saveMarketGoal: (body: MarketGoal) => api.put('/win-the-day/settings/market-goal', body),
  saveDispatcherGoal: (body: DispatcherGoal) => api.put('/win-the-day/settings/dispatcher-goal', body),
  saveThreshold: (body: Threshold) => api.put('/win-the-day/settings/threshold', body),
  saveWeights: (body: Weights) => api.put('/win-the-day/settings/weights', body),
  // Market conditions live at their own top-level resource, not under Win the Day.
  conditions: async (start: string, end: string) =>
    (await api.get<{ rows: DailyCondition[] }>('/market-conditions/dated', { params: { start, end } })).data.rows,
  // Written fields only, spelled out: the read shape carries `images` as saved
  // {id, name} records while the write takes new screenshots as data URLs, so
  // spreading a loaded row into the body would send the wrong kind.
  saveConditions: (body: DailyCondition, images: string[] = [], keepImageIds: number[] = []) =>
    api.put('/market-conditions/dated', {
      market: body.market, day: body.day, conditions: body.conditions,
      condition_status: body.condition_status, days_out: body.days_out,
      images, keep_image_ids: keepImageIds,
    }),
};
